import React from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

type AvaliacaoDTO = {
  id: number;
  nota: number;
  comentario?: string;
  usuarioNome?: string;
  usuarioFotoUrl?: string;
  fotosPorPonto?: { fotoId: number; ponto: string; url: string }[];
  fotos?: number[];
};

type VeiculoDTO = {
  veiculoId: number;
  modelo: string;
  placa: string;
  capacidade: number;
  fotoUrl?: string;
};

type MotoristaDTO = {
  id: number;
  nome: string;
  idiomas?: string[];
  fotoId?: number;
  veiculo?: VeiculoDTO;
  avaliacoes: AvaliacaoDTO[];
  descricao?: string;
};

type Props = {
  motorista: MotoristaDTO;
  open: boolean;
  onClose: () => void;
  baseUrl: string;
};

const DriverReviewsModal: React.FC<Props> = ({ motorista, open, onClose, baseUrl }) => {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxSlides, setLightboxSlides] = React.useState<{ src: string }[]>([]);

  const openLightbox = (slides: { src: string }[]) => {
    setLightboxSlides(slides);
    setLightboxOpen(true);
  };

  const calcularMedia = (avaliacoes: AvaliacaoDTO[]) => {
    if (!avaliacoes || avaliacoes.length === 0) return 0;
    const soma = avaliacoes.reduce((acc, a) => acc + (a.nota || 0), 0);
    return soma / avaliacoes.length;
  };

  const media = calcularMedia(motorista.avaliacoes);

  return (
    <>
      {/* Modal */}
      <div className={`modal fade ${open ? "show d-block" : ""}`} tabIndex={-1} aria-hidden={!open}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{motorista.nome}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {/* Info do motorista */}
              <div className="d-flex mb-3 align-items-center gap-3">
                <img
                  src={
                    motorista.fotoId
                      ? `${baseUrl}/uploads/${motorista.fotoId}`
                      : motorista.veiculo?.fotoUrl
                        ? `${baseUrl}${motorista.veiculo.fotoUrl}`
                        : "assets/img/users/default-user.jpg"
                  }
                  alt={motorista.nome}
                  className="avatar avatar-xl rounded-circle"
                />
                <div>
                  {motorista.descricao && (
                    <p className="mb-1"><strong>Description:</strong> {motorista.descricao}</p>
                  )}
                  <p className="mb-1"><strong>Languages:</strong> {motorista.idiomas?.join(", ") || "N/A"}</p>
                  <p className="mb-0">
                    <strong>Vehicle:</strong> {motorista.veiculo?.modelo || "N/A"} ({motorista.veiculo?.capacidade || "N/A"} seats)
                  </p>
                  <p className="mb-0">
                    <strong>Average Rating:</strong>{" "}
                    {motorista.avaliacoes.length > 0 ? (
                      <span className="badge badge-warning badge-xs text-gray-9 fs-10 fw-medium me-2 p-1">
                        <i className="isax isax-star" /> {media.toFixed(1)}
                      </span>
                    ) : (
                      "No reviews yet"
                    )}
                  </p>
                </div>
              </div>

              {/* Avaliações */}
              <div className="mt-3">
                {motorista.avaliacoes.length === 0 ? (
                  <p>No reviews yet.</p>
                ) : (
                  motorista.avaliacoes.map((av) => (
                    <div key={av.id} className="card mb-2 p-2">
                      <div className="d-flex align-items-center mb-2">
                        <img
                          src={av.usuarioFotoUrl ? `${baseUrl}${av.usuarioFotoUrl}` : "/assets/img/users/default-avatar.png"}
                          className="avatar rounded-circle me-2"
                          alt={av.usuarioNome}
                          style={{ width: "40px", height: "40px" }}
                        />
                        <div>
                          <strong>{av.usuarioNome}</strong>
                          <span className="badge badge-warning badge-xs text-gray-9 fs-10 fw-medium ms-2 p-1">
                            <i className="isax isax-star" /> {av.nota.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <p style={{ fontSize: "0.9rem", lineHeight: 1.4 }}>{av.comentario}</p>

                      {/* Fotos da avaliação */}
                      {(av.fotosPorPonto?.length || 0) > 0 || (av.fotos?.length || 0) > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {av.fotosPorPonto?.map((f) => (
                            <div
                              key={`ponto-${f.fotoId}`}
                              style={{ width: "80px", height: "80px", cursor: "pointer", position: "relative" }}
                              onClick={() =>
                                openLightbox([
                                  ...(av.fotos?.map((fid) => ({ src: `${baseUrl}/uploads/${fid}` })) || []),
                                  ...(av.fotosPorPonto?.map((fp) => ({ src: `${baseUrl}${fp.url}` })) || []),
                                ])
                              }
                            >
                              <img
                                src={`${baseUrl}${f.url}`}
                                alt={f.ponto || "foto"}
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  width: "100%",
                                  background: "rgba(0,0,0,0.5)",
                                  color: "#fff",
                                  fontSize: "0.7rem",
                                  textAlign: "center",
                                  borderBottomLeftRadius: "6px",
                                  borderBottomRightRadius: "6px",
                                }}
                              >
                                {f.ponto}
                              </div>
                            </div>
                          ))}
                          {av.fotos?.map((fid) => (
                            <div
                              key={`foto-${fid}`}
                              style={{ width: "80px", height: "80px", cursor: "pointer" }}
                              onClick={() =>
                                openLightbox([
                                  ...(av.fotos?.map((fid2) => ({ src: `${baseUrl}/uploads/${fid2}` })) || []),
                                  ...(av.fotosPorPonto?.map((fp) => ({ src: `${baseUrl}${fp.url}` })) || []),
                                ])
                              }
                            >
                              <img
                                src={`${baseUrl}/uploads/${fid}`}
                                alt="foto"
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
                              />
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop escuro */}
      {open && <div className="modal-backdrop fade show"></div>}

      {/* Lightbox */}
      <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} slides={lightboxSlides} />
    </>
  );
};

export default DriverReviewsModal;